/**
 * Preloader — анимированный логотип «УмникAI» для лоадинга.
 *
 * Структура:
 *   .preloader__web  — паутина (линии + 12 узлов + ~100 квадратиков «пыли»),
 *                      анимируется в зависимости от variant.
 *   .preloader__core — центральный значок «АИ» (плашка + рамка + буквы),
 *                      статичен.
 *
 * 2 варианта анимации:
 *   1 — «Искры от перегоревшего провода»: пылинки россыпи вспыхивают
 *       по очереди от точки разрыва наружу, потом пауза. Сам spine
 *       неподвижен.
 *   2 — Бильярдные удары волной по часовой стрелке от верхней пары.
 *       Цикл 3s, перекрытие пар 1.1s — плотный непрерывный поток
 *       ударов с видимым направлением.
 *
 * SVG-defs оптимизированы (1 filter + 3 градиента) — оригинальный логотип
 * имел 25 идентичных filter'ов и 99 идентичных «пыльных» градиентов.
 */
// Стили в /styles/components/_preloader.scss, подключены через global.scss

interface Props {
	size?: number
	variant?: '1' | '2'
	className?: string
}

// ── Координаты 12 узлов-кружков (большой белый + цветной overlay) ──
// [cx, cy, rOuter, rInner, color]
const NODES: Array<[number, number, number, number, string]> = [
	[126.798, 475.648, 14.226, 9.278, '#5B73F7'],
	[304.935, 567.191, 14.226, 9.278, '#935EF7'],
	[304.935, 181.229, 14.226, 9.278, '#5B73F7'],
	[124.324, 271.534, 14.226, 9.278, '#51A8FF'],
	[481.834, 275.245, 14.226, 9.278, '#9B5DF6'],
	[485.545, 474.411, 14.226, 9.278, '#BC58EB'],
	[305.553, 629.662, 19.793, 14.844, '#AC5BF0'],
	[72.9863, 504.719, 19.793, 14.844, '#54A2FF'],
	[72.9863, 243.7, 19.793, 14.844, '#39D1FF'],
	[305.553, 118.758, 19.793, 14.844, '#51A8FF'],
	[539.357, 243.7, 19.793, 14.844, '#B35AEE'],
	[539.357, 504.719, 19.793, 14.844, '#E952DE'],
]

// ── Координаты «пыли» (мелкие квадратики на фоне) ──
// [x, y, w, h]. Большая часть 12.371×12.371, остальные мельче.
const DUST: Array<[number, number, number, number]> = [
	[119.994, 334.005, 12.371, 12.371],
	[92.7792, 387.199, 12.371, 12.371],
	[80.4087, 334.005, 12.371, 12.371],
	[81.6457, 366.169, 12.371, 12.371],
	[63.0899, 373.591, 12.371, 12.371],
	[98.9645, 383.487, 12.371, 12.371],
	[124.943, 337.716, 12.371, 12.371],
	[112.572, 351.324, 12.371, 12.371],
	[97.7275, 306.79, 12.371, 12.371],
	[155.869, 238.752, 12.371, 12.371],
	[171.951, 205.351, 12.371, 12.371],
	[200.403, 192.981, 12.371, 12.371],
	[195.455, 178.136, 12.371, 12.371],
	[170.714, 167.003, 12.371, 12.371],
	[141.024, 209.063, 12.371, 12.371],
	[111.335, 184.321, 12.371, 12.371],
	[82.8828, 211.537, 12.371, 12.371],
	[68.0381, 202.877, 12.371, 12.371],
	[53.1934, 306.79, 12.371, 12.371],
	[131.128, 231.33, 12.371, 12.371],
	[86.5939, 132.365, 12.371, 12.371],
	[97.7275, 199.166, 12.371, 12.371],
	[13.6076, 301.842, 12.371, 12.371],
	[58.1416, 361.22, 7.4223, 7.4223],
	[39.5858, 264.73, 7.4223, 7.4223],
	[9.89645, 226.381, 7.4223, 7.4223],
	[138.55, 195.455, 7.4223, 7.4223],
	[176.899, 223.907, 7.4223, 7.4223],
	[202.877, 186.796, 7.4223, 7.4223],
	[133.602, 159.58, 7.4223, 7.4223],
	[197.929, 126.18, 7.4223, 7.4223],
	[110.098, 69.2752, 7.4223, 7.4223],
	[167.003, 103.913, 7.4223, 7.4223],
	[169.477, 142.261, 7.4223, 7.4223],
	[184.321, 191.744, 7.4223, 7.4223],
	[37.1117, 34.6376, 7.4223, 7.4223],
	[106.387, 0, 7.4223, 7.4223],
	[70.5122, 27.2152, 7.4223, 7.4223],
	[65.564, 79.1716, 7.4223, 7.4223],
	[6.18528, 178.136, 7.4223, 7.4223],
	[58.1416, 279.575, 7.4223, 7.4223],
	[59.3787, 294.419, 7.4223, 7.4223],
	[76.6975, 309.264, 7.4223, 7.4223],
	[48.2452, 324.109, 7.4223, 7.4223],
	[44.534, 345.139, 7.4223, 7.4223],
	[81.6457, 355.035, 7.4223, 7.4223],
	[79.1716, 338.953, 7.4223, 7.4223],
	[89.0681, 350.087, 7.4223, 7.4223],
	[91.5422, 351.324, 7.4223, 7.4223],
	[94.0163, 357.509, 7.4223, 7.4223],
	[17.3188, 76.6975, 7.4223, 7.4223],
	[132.365, 118.757, 7.4223, 7.4223],
	[55.6675, 118.757, 7.4223, 7.4223],
	[69.2752, 174.425, 7.4223, 7.4223],
	[77.9346, 205.351, 7.4223, 7.4223],
	[103.913, 195.455, 7.4223, 7.4223],
	[91.5422, 218.959, 7.4223, 7.4223],
	[105.15, 206.588, 7.4223, 7.4223],
	[122.469, 204.114, 7.4223, 7.4223],
	[141.024, 233.804, 7.4223, 7.4223],
	[69.2752, 142.261, 7.4223, 7.4223],
	[33.4005, 275.864, 7.4223, 7.4223],
	[23.5041, 300.605, 7.4223, 7.4223],
	[23.5041, 319.161, 7.4223, 7.4223],
	[92.7792, 399.569, 7.4223, 7.4223],
	[70.5122, 403.28, 7.4223, 7.4223],
	[65.564, 393.384, 7.4223, 7.4223],
	[100.202, 368.643, 7.4223, 7.4223],
	[124.943, 322.872, 7.4223, 7.4223],
	[124.943, 316.686, 7.4223, 7.4223],
	[75.4604, 312.975, 7.4223, 7.4223],
	[75.4604, 301.842, 7.4223, 7.4223],
	[178.755, 248.648, 3.7112, 3.7112],
	[199.166, 227.618, 3.7112, 3.7112],
	[216.485, 205.351, 3.7112, 3.7112],
	[159.58, 188.033, 3.7112, 3.7112],
	[163.291, 167.003, 3.7112, 3.7112],
	[47.0081, 163.291, 3.7112, 3.7112],
	[58.1416, 192.981, 3.7112, 3.7112],
	[43.297, 211.537, 3.7112, 3.7112],
	[21.03, 275.864, 3.7112, 3.7112],
	[39.5858, 298.131, 3.7112, 3.7112],
	[37.1117, 321.635, 3.7112, 3.7112],
	[98.9645, 325.346, 3.7112, 3.7112],
	[49.4823, 387.199, 3.7112, 3.7112],
	[45.7711, 350.087, 3.7112, 3.7112],
	[77.9346, 372.354, 3.7112, 3.7112],
	[138.55, 399.569, 3.7112, 3.7112],
	[137.313, 367.406, 3.7112, 3.7112],
	[106.387, 348.85, 3.7112, 3.7112],
	[96.4904, 289.471, 3.7112, 3.7112],
	[22.267, 184.321, 3.7112, 3.7112],
	[23.5041, 138.55, 3.7112, 3.7112],
	[0, 111.335, 3.7112, 3.7112],
	[33.4005, 76.6975, 3.7112, 3.7112],
	[72.9863, 48.2452, 3.7112, 3.7112],
	[90.3051, 95.2533, 3.7112, 3.7112],
	[101.439, 107.624, 3.7112, 3.7112],
]

// ── Главный path: линии-перья паутины ──
const WEB_SPINE_D =
	'M119.994 406.256L130.509 411.59V468.806L257.868 399.067L281.487 349.468H252.955L130.509 281.636V320.546L119.994 312.975V275.864L78.5527 254.503V294.18L68.6562 288.434V238.219L124.942 209.005L135.125 214.869L79.4902 243.745L121.85 265.546L158.961 246.753L172.569 252.027L134.594 270.915L255.475 339.572H286.199L299.986 310.62V188.651L246.174 215.417L233.504 209.005L300.57 175.044L299.986 129.303L216.619 172.572L205.97 166.951L300.015 118.139H316.715L533.69 236.885L534.246 237.189L538.032 240.752L539.463 239.973L541.841 244.338L546.779 248.984V506.453L542.957 508.487L541.981 510.326L540.743 509.667L306.155 634.658L68.6562 506.433V402.662L78.5527 407.483V495.213L119.994 473.793V406.256ZM310.545 575.231L311.066 620.827L530.208 504.067L490.492 481.215L310.545 575.231ZM84.047 503.496L301.168 620.718L300.644 575.231L127.416 481.215L84.047 503.496ZM359.389 349.468H292.447L265.406 406.256L137.397 476.267L300.57 564.716L299.986 438.569L309.882 438.504L310.473 564.716L479.977 476.267L355.804 411.361L360.451 402.622L479.977 465.719V282.049L359.389 349.468ZM490.492 276.2V469.463L536.882 496.405V253.26L536.474 252.877L490.492 276.2ZM309.882 312.856L297.16 339.572H356.866L471.318 276.2L309.882 188.651V312.856ZM309.882 175.044L482.452 270.047L528.886 245.735L528.383 245.261L314.184 128.035H309.882V175.044Z'

// ── Центр: плашка + рамка + буквы ──
const CORE_PLATE_D =
	'M202.632 288.32C202.632 280.215 209.234 273.645 217.378 273.645H388.804C396.949 273.645 403.551 280.215 403.551 288.32V458.905C403.551 467.01 396.949 473.579 388.804 473.579H217.378C209.234 473.579 202.632 467.01 202.632 458.905V288.32Z'

const CORE_FRAME_D =
	'M230.595 479.082V508.43H222.595V479.082H230.595ZM258.244 479.082V508.43H250.244V479.082H258.244ZM284.05 479.082V508.43H276.05V479.082H284.05ZM333.819 479.082V508.43H325.819V479.082H333.819ZM357.782 479.082V508.43H349.782V479.082H357.782ZM380.823 479.082V508.43H372.823V479.082H380.823ZM233.359 242.463V269.646H248.4V242.463H256.4V269.646H272.363V242.463H280.363V269.646H322.133V242.463H330.133V269.646H347.938V242.463H355.938V269.646H375.588V242.463H383.588V269.646H388.805C399.139 269.646 407.551 277.988 407.551 288.32V458.906C407.551 469.237 399.139 477.579 388.805 477.579H217.378C207.043 477.579 198.632 469.237 198.632 458.906V288.32C198.632 277.988 207.043 269.646 217.378 269.646H225.359V242.463H233.359ZM444.104 400.795V408.795H413.177V400.795H444.104ZM195.259 397.126V405.126H165.766V397.126H195.259ZM444.104 376.949V384.949H413.177V376.949H444.104ZM195.259 374.198V382.198H165.766V374.198H195.259ZM444.104 354.022V362.022H413.177V354.022H444.104ZM195.259 350.353V358.353H165.766V350.353H195.259Z'

const CORE_LETTER_A_D =
	'M257.284 416.888H236.277L268.285 325.346H293.547L325.509 416.888H304.503L281.278 346.265H280.554L257.284 416.888ZM255.971 380.906H305.589V396.014H255.971V380.906Z'

const CORE_LETTER_I_D = 'M356.272 325.346V416.888H336.669V325.346H356.272Z'

// ── Удобные wrapper'ы для прямого использования в проекте ──────────
// Универсальные компоненты — место использования не должно знать про
// variant'ы. Если нужен спокойный лоадер (загрузка данных, idle state) —
// импортируется PreloaderCalm. Если динамический (генерация контента) —
// PreloaderDynamic. Базовый <Preloader /> с prop variant остаётся для
// специальных кейсов и demo-страницы.

type WrapperProps = Omit<Props, 'variant'>

/**
 * PreloaderCalm — спокойный прелоадер: «искры от перегоревшего провода».
 * Россыпь россыпь периодически вспыхивает волной от точки разрыва наружу,
 * паутина дёргается короткими провалами яркости. Подходит когда нужна
 * мягкая индикация ожидания, не отвлекающая от контента.
 */
export function PreloaderCalm(props: WrapperProps) {
	return <Preloader {...props} variant="1" />
}

/**
 * PreloaderDynamic — динамический прелоадер для генерации контента.
 * Бильярдные удары волной по часовой стрелке от верхней пары — плотный
 * непрерывный поток с видимым направлением. Используется в виджете,
 * на странице /generate, в /bots/* при создании контента.
 */
export function PreloaderDynamic(props: WrapperProps) {
	return <Preloader {...props} variant="2" />
}

export default function Preloader({ size = 120, variant = '1', className }: Props) {
	const cls = `preloader preloader--${variant}${className ? ' ' + className : ''}`
	return (
		<svg
			width={size}
			height={size}
			// viewBox сдвинут (22 45), чтобы ЦЕНТР ГЕКСАГОНА+ядра (306, 374)
			// совпал с центром рамки — иначе лого везде рендерилось правее/ниже
			// центра контейнера (пыль слева раздувала композицию). Пыль теперь
			// частично за рамкой — это декор, overflow: visible её рисует.
			// Бонус: transform-origin 284px/329px (view-box-относительный)
			// теперь указывает в истинный центр гексагона → удары симметричны.
			viewBox="22 45 568 658"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
			className={cls}
			role="img"
			aria-label="Загрузка"
		>
			<defs>
				{/* Один drop-shadow filter для всех светящихся элементов.
				    Цепочка: alpha → blur → tint → blend с оригиналом.
				    Последний feBlend критичен — без него filter рендерит
				    ТОЛЬКО размытую тень, а оригинальный fill (включая
				    градиент паутины) теряется. */}
				<filter id="pl-glow" x="-20%" y="-20%" width="140%" height="140%">
					<feGaussianBlur in="SourceAlpha" stdDeviation="4" result="blur" />
					<feColorMatrix
						in="blur"
						type="matrix"
						values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.12 0"
						result="shadow"
					/>
					<feBlend mode="normal" in="SourceGraphic" in2="shadow" />
				</filter>
				{/* Главный градиент линий паутины */}
				<linearGradient
					id="pl-main"
					x1="149.065"
					y1="123.706"
					x2="632.344"
					y2="433.437"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#35D7FF" />
					<stop offset="0.5" stopColor="#7B61FF" />
					<stop offset="1" stopColor="#FF4FD8" />
				</linearGradient>
				{/* Градиент рамки центра */}
				<linearGradient
					id="pl-frame"
					x1="212.575"
					y1="245.33"
					x2="472.838"
					y2="433.907"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#35D7FF" />
					<stop offset="0.5" stopColor="#7B61FF" />
					<stop offset="1" stopColor="#FF4FD8" />
				</linearGradient>
				{/* Градиент мелкой пыли — голубой, fade сверху */}
				<linearGradient
					id="pl-dust"
					x1="61.2343"
					y1="-6.18528"
					x2="110.098"
					y2="410.703"
					gradientUnits="userSpaceOnUse"
				>
					<stop stopColor="#3AA6EE" stopOpacity="0" />
					<stop offset="0.514052" stopColor="#3CCBFF" />
					<stop offset="1" stopColor="#49B6FF" />
				</linearGradient>
			</defs>

			{/* ── WEB — анимируется ── */}
			<g className="preloader__web">
				{/* Линии паутины (главные «лучи») */}
				<g className="preloader__spine">
					<path d={WEB_SPINE_D} fill="url(#pl-main)" filter="url(#pl-glow)" />
				</g>

				{/* 12 узлов-шариков — простые белые круги с мягким glow.
				    Делим на 2 кольца (small / large) и считаем pair-index
				    по углу относительно центра — для wave-эффекта в strike-
				    варианте (бильярдный удар бежит по периметру). */}
				<g className="preloader__nodes">
					{NODES.map(([cx, cy, rOuter], i) => {
						const isLarge = rOuter > 17
						const cls = `preloader__node preloader__node--${isLarge ? 'large' : 'small'}`
						// Угол относительно центра viewBox (284, 329).
						// SVG-Y направлена вниз, поэтому atan2(cy-329, cx-284):
						//   верх    → -π/2
						//   право   →  0
						//   низ     → +π/2
						//   лево    →  π
						// Сдвигаем на +π/2 чтобы 0 = ВЕРХ, потом нумерация
						// по часовой стрелке (через право). 6 секторов = 6 пар.
						// Используется для wave-delay в strike-вариантах.
						const angle = Math.atan2(cy - 329, cx - 284)
						const shifted = (angle + Math.PI / 2 + 2 * Math.PI) % (2 * Math.PI)
						const pairIdx = Math.round((shifted / (2 * Math.PI)) * 6) % 6
						return (
							<g
								key={i}
								className={cls}
								filter="url(#pl-glow)"
								style={{
									['--n' as string]: i,
									['--p' as string]: pairIdx,
								}}
							>
								<circle cx={cx} cy={cy} r={rOuter} fill="white" />
							</g>
						)
					})}
				</g>

				{/* Пыль. --i здесь — индекс в порядке от точки разрыва
				    (центр россыпи ≈ 110, 200) к самым дальним пылинкам.
				    Это даёт физически правильную волну «искр»: они
				    вспыхивают сначала ближе к проводу, потом разлетаются.
				    Sort стабилен по index при равных дистанциях. */}
				<g className="preloader__dust">
					{DUST
						.map((d, origIdx) => {
							const dist = Math.hypot(d[0] - 110, d[1] - 200)
							return { d, origIdx, dist }
						})
						.sort((a, b) => a.dist - b.dist)
						.map(({ d, origIdx }, i) => {
							const [x, y, w, h] = d
							return (
								<rect
									key={origIdx}
									x={x}
									y={y}
									width={w}
									height={h}
									fill="url(#pl-dust)"
									style={{ ['--i' as string]: i }}
								/>
							)
						})}
				</g>
			</g>

			{/* ── CORE — статичен ── */}
			<g className="preloader__core">
				<path d={CORE_PLATE_D} fill="#3C218C" />
				<path
					d={CORE_FRAME_D}
					stroke="url(#pl-frame)"
					strokeWidth="8"
				/>
				<path d={CORE_LETTER_A_D} fill="white" />
				<path d={CORE_LETTER_I_D} fill="white" />
			</g>
		</svg>
	)
}
